import React, { useState, useRef, useEffect } from 'react';
import { User } from '../../services/authService';
import PageHeader from '../common/PageHeader';
import CharacterWritingCanvas, { CharacterWritingCanvasRef } from '../common/CharacterWritingCanvas';
import {
  generateRandomCharacter,
  verifyCharacter,
  savePracticeAttempt,
  getPracticeHistory,
  CharacterVerificationResult,
  PracticeAttempt,
  GeneratedCharacter
} from '../../services/chineseCharacterService';
import './ChineseCharacter.css';

interface ChineseCharacterProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const ChineseCharacter: React.FC<ChineseCharacterProps> = ({
  user,
  onToggleSidebar
}) => {
  const canvasRef = useRef<CharacterWritingCanvasRef>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  
  const [selectedCharacter, setSelectedCharacter] = useState<GeneratedCharacter | null>(null);
  const [brushSize, setBrushSize] = useState(10);
  const [showTemplate, setShowTemplate] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [verificationResult, setVerificationResult] = useState<CharacterVerificationResult | null>(null);
  const [practiceHistory, setPracticeHistory] = useState<PracticeAttempt[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [characterFilter, setCharacterFilter] = useState<string>('');


  // Load practice history on mount
  useEffect(() => {
    loadPracticeHistory();
  }, [characterFilter]);

  const loadPracticeHistory = async () => {
    try {
      const result = await getPracticeHistory(20, 0, characterFilter || undefined);
      setPracticeHistory(result.attempts);
    } catch (error) {
      console.error('Failed to load practice history:', error);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const clearCanvas = () => {
    canvasRef.current?.clear();
    setVerificationResult(null);
  };

  const handleGenerateCharacter = async () => {
    setIsGenerating(true);
    setVerificationResult(null);
    
    try {
      const character = await generateRandomCharacter();
      setSelectedCharacter(character);
      clearCanvas();
    } catch (error) {
      console.error('Failed to generate character:', error);
      alert('Failed to generate character. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedCharacter || !canvasRef.current) return;

    if (!canvasRef.current.hasDrawing()) {
      alert('Please draw a character before submitting.');
      return;
    }

    const imageData = canvasRef.current.getImageData();
    if (!imageData) {
      alert('Failed to get image data. Please try again.');
      return;
    }

    setIsVerifying(true);
    setVerificationResult(null);

    try {
      // Verify character
      const result = await verifyCharacter(imageData, selectedCharacter.character);
      setVerificationResult(result);

      // Save practice attempt
      await savePracticeAttempt({
        character: selectedCharacter.character,
        image_data: imageData,
        is_correct: result.is_correct,
        score: result.confidence_score,
        feedback: result.feedback
      });

      // Reload history
      await loadPracticeHistory();
    } catch (error) {
      console.error('Failed to verify character:', error);
      alert('Failed to verify character. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleShowHistory = () => {
    setShowMenu(false);
    setShowHistory(!showHistory);
  };

  const handleClearFilter = () => {
    setCharacterFilter('');
  };

  return (
    <div className="chinese-character-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <h1 className="page-header-center-title">
            Chinese Character Writing
          </h1>
        }
        rightActions={
          <div className="chinese-character-menu-container" ref={menuRef}>
            <button
              className="page-header-icon-btn"
              onClick={() => setShowMenu(!showMenu)}
            >
              ⋮
            </button>
            {showMenu && (
              <div className="chinese-character-menu-dropdown">
                <button
                  className="chinese-character-menu-item"
                  onClick={handleShowHistory}
                >
                  {showHistory ? '📚 Hide History' : '📚 Show History'}
                </button>
              </div>
            )}
          </div>
        }
      />

      <div className="chinese-character-content">
        {/* Character Generator */}
        <div className="chinese-character-section">
          <div className="chinese-character-section-header">/character</div>
          <div className="chinese-character-generator">
            <button
              className="chinese-character-generate-btn"
              onClick={handleGenerateCharacter}
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating...' : '🎲 Generate Random Character'}
            </button>
            {selectedCharacter && (
              <div className="chinese-character-display">
                <div className="chinese-character-char">{selectedCharacter.character}</div>
                <div className="chinese-character-info">
                  <span className="chinese-character-pinyin">{selectedCharacter.pinyin}</span>
                  <span className="chinese-character-meaning">{selectedCharacter.meaning}</span>
                  {selectedCharacter.difficulty && (
                    <span className="chinese-character-difficulty">{selectedCharacter.difficulty}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Drawing Area */}
        {selectedCharacter && (
          <div className="chinese-character-drawing-section">
            <div className="chinese-character-section-header">/practice</div>
            <div className="chinese-character-drawing-area">
              <CharacterWritingCanvas
                ref={canvasRef}
                character={selectedCharacter.character}
                showTemplate={showTemplate}
                brushSize={brushSize}
                templateFont='200px "Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif'
              />
              <div className="chinese-character-controls">
                <div className="chinese-character-control-group">
                  <label className="chinese-character-control-label">Brush Size</label>
                  <input
                    type="range"
                    min="6"
                    max="16"
                    value={brushSize}
                    onChange={(e) => setBrushSize(Number(e.target.value))}
                    className="chinese-character-slider"
                  />
                  <span className="chinese-character-slider-value">{brushSize}px</span>
                </div>
                <button
                  className="chinese-character-control-btn"
                  onClick={() => setShowTemplate(!showTemplate)}
                >
                  {showTemplate ? '👁️ Hide Template' : '👁️ Show Template'}
                </button>
                <button
                  className="chinese-character-control-btn"
                  onClick={clearCanvas}
                >
                  Clear
                </button>
                <button
                  className="chinese-character-control-btn chinese-character-submit-btn"
                  onClick={handleSubmit}
                  disabled={isVerifying}
                >
                  {isVerifying ? 'Verifying...' : 'Submit'}
                </button>
              </div>
            </div>

            {/* Verification Result */}
            {verificationResult && (
              <div className={`chinese-character-result ${verificationResult.is_correct ? 'correct' : 'incorrect'}`}>
                <div className="chinese-character-result-header">
                  <span className="chinese-character-result-status">
                    {verificationResult.is_correct ? '✓ Correct' : '✗ Incorrect'}
                  </span>
                  <span className="chinese-character-result-score">
                    Score: {(verificationResult.confidence_score ?? 0).toFixed(0)}%
                  </span>
                </div>
                <div className="chinese-character-result-feedback">
                  {verificationResult.feedback}
                </div>
                {verificationResult.suggestions && verificationResult.suggestions.length > 0 && (
                  <div className="chinese-character-result-suggestions">
                    <strong>Suggestions:</strong>
                    <ul>
                      {verificationResult.suggestions.map((suggestion, index) => (
                        <li key={index}>{suggestion}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Practice History */}
        {showHistory && (
          <div className="chinese-character-history-section">
            <div className="chinese-character-section-header">/history</div>
            <div className="chinese-character-history-controls">
              <input
                type="text"
                placeholder="Filter by character..."
                value={characterFilter}
                onChange={(e) => setCharacterFilter(e.target.value)}
                className="chinese-character-filter-input"
              />
              {characterFilter && (
                <button
                  className="chinese-character-control-btn"
                  onClick={handleClearFilter}
                >
                  Clear Filter
                </button>
              )}
            </div>
            <div className="chinese-character-history-list">
              {practiceHistory.length === 0 ? (
                <div className="chinese-character-history-empty">
                  No practice attempts yet. Start practicing to see your history here.
                </div>
              ) : (
                practiceHistory.map((attempt) => (
                  <div
                    key={attempt.id}
                    className={`chinese-character-history-item ${attempt.is_correct ? 'correct' : 'incorrect'}`}
                  >
                    <div className="chinese-character-history-char">{attempt.character}</div>
                    <div className="chinese-character-history-details">
                      <span className="chinese-character-history-score">
                        {(attempt.score ?? 0).toFixed(0)}%
                      </span>
                      <span className="chinese-character-history-status">
                        {attempt.is_correct ? '✓' : '✗'}
                      </span>
                      <span className="chinese-character-history-date">
                        {attempt.created_at
                          ? new Date(attempt.created_at).toLocaleDateString()
                          : ''}
                      </span>
                    </div>
                    <div className="chinese-character-history-feedback">{attempt.feedback}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChineseCharacter;

